import React, { useState } from 'react';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Calendar } from '@fiverr-private/calendar';
import { Gql } from '@fiverr-private/dynamic_matching';
import { DAY_IN_MS } from '@fiverr-private/futile';
import { Radio, RadioGroup } from '@fiverr-private/forms';
import { DATETIME_ELEMENT_STYLES, dateTimeFormat } from '@fiverr-private/localization';
import { atoms } from '@fiverr-private/theme';
import { Dropdown, DropdownAnchor, DropdownContent } from '@fiverr-private/dropdown';
import { AppI18n, appTranslate } from '../../../i18n';
import { EditComponentProps, TimelineContentType } from '..';
import { EditActions } from '../EditActions';
import { CalendarTrigger } from './CalendarTrigger';

const tomorrow = new Date(new Date().getTime() + DAY_IN_MS);

const spacingForControls = atoms({
  paddingTop: '3',
});

export const EditTimeline = ({ section, onChange, onEditEnd, onCancel }: EditComponentProps) => {
  const content = section?.content as unknown as TimelineContentType;

  const [timelineContent, setTimelineContent] = useState<TimelineContentType>(
    content || {
      timeline: null,
      deliveryDate: null,
    }
  );

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const [selectedDate, setSelectedDate] = useState<Date | null>(
    timelineContent?.deliveryDate ? new Date(timelineContent.deliveryDate) : tomorrow
  );

  const getFormattedDate = () =>
    timelineContent.deliveryDate
      ? dateTimeFormat({
          value: Number(timelineContent.deliveryDate),
          options: {
            day: DATETIME_ELEMENT_STYLES.TWO_DIGIT,
            month: DATETIME_ELEMENT_STYLES.TWO_DIGIT,
            year: DATETIME_ELEMENT_STYLES.NUMERIC,
          },
        })
      : '';

  const onChangedTimeline = (timeline: string) => {
    setTimelineContent({
      timeline,
      deliveryDate: timelineContent.deliveryDate,
    } as TimelineContentType);

    if (timeline === Gql.DmBriefTimeline.SpecificDate && !timelineContent.deliveryDate) {
      return;
    }

    onChange({
      timeline,
      deliveryDate: timeline === Gql.DmBriefTimeline.SpecificDate ? timelineContent.deliveryDate : undefined,
    } as any);
  };

  const onChangedSpecificDate = (date: Date | null) => {
    const newValue = {
      timeline: timelineContent.timeline,
      deliveryDate: date?.getTime() || tomorrow.getTime(),
    } as TimelineContentType;

    setSelectedDate(date || tomorrow);
    setTimelineContent(newValue);

    if (newValue?.timeline && newValue?.deliveryDate) {
      onChange(newValue as any);
    }

    setIsCalendarOpen(false);
  };

  const onCalendarOpenChange = (isOpen: boolean) => {
    setIsCalendarOpen(isOpen);
  };

  const hideDateControl = timelineContent?.timeline !== Gql.DmBriefTimeline.SpecificDate;

  const isDisabled = (): boolean =>
    !timelineContent.timeline ||
    (timelineContent.timeline === Gql.DmBriefTimeline.SpecificDate && !timelineContent.deliveryDate);

  return (
    <Stack direction="column">
      <Container paddingTop="1">
        <RadioGroup name="group-controlled" value={timelineContent.timeline} onChange={onChangedTimeline}>
          <Radio
            className={spacingForControls}
            name={Gql.DmBriefTimeline.SoonAsPossible}
            value={Gql.DmBriefTimeline.SoonAsPossible}
          >
            <AppI18n k="sections.timeline.asap" />
          </Radio>
          <Radio
            className={spacingForControls}
            name={Gql.DmBriefTimeline.SpecificDate}
            value={Gql.DmBriefTimeline.SpecificDate}
          >
            <AppI18n k="sections.timeline.by_specific_date" />
          </Radio>
          <Container className={spacingForControls} hidden={hideDateControl} width="240px">
            <Dropdown isOpen={isCalendarOpen} onOpenChanged={onCalendarOpenChange}>
              <DropdownAnchor fullWidth>
                <CalendarTrigger
                  placeholder={appTranslate('sections.timeline.choose_date') as string}
                  value={getFormattedDate()}
                />
              </DropdownAnchor>
              <DropdownContent>
                <Calendar
                  allowedDatesRange={{
                    min: tomorrow,
                  }}
                  date={selectedDate}
                  calendarMode="single"
                  onDateChanged={onChangedSpecificDate}
                />
              </DropdownContent>
            </Dropdown>
          </Container>
          <Radio
            className={spacingForControls}
            name={Gql.DmBriefTimeline.Flexible}
            value={Gql.DmBriefTimeline.Flexible}
          >
            <AppI18n k="sections.timeline.flexible" />
          </Radio>
          <Radio className={spacingForControls} name={Gql.DmBriefTimeline.Ongoing} value={Gql.DmBriefTimeline.Ongoing}>
            <AppI18n k="sections.timeline.ongoing_need" />
          </Radio>
        </RadioGroup>
      </Container>
      <EditActions onCancel={onCancel} onEditEnd={onEditEnd} isSaveDisabled={isDisabled()} />
    </Stack>
  );
};
